"use client";

import { createImageSizes, createImageUrl } from "@/utils/image";
import Image from "next/image";
import Script from "next/script";
import { useRef, useState, type MouseEvent } from "react";
import Icon from "./icon";

type Props = {
  imageUrl: string;
  src: string;
  appVersion: string;
  videoTitle: string;
  isApp: boolean;
  streamUrl?: string | null;
  duration?: number | null;
  isLive?: boolean;
};

export default function ArticleVideoPlayer({
  imageUrl,
  src,
  videoTitle,
  isApp,
  streamUrl,
  duration,
  isLive,
  appVersion,
}: Props) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [, setSdkReady] = useState(false);

  if (isApp && appVersion !== "1.0") {
    function handleOverlayClick(event: MouseEvent) {
      event.preventDefault();
      event.stopPropagation();
      window.postMessage(
        {
          type: "play",
          videoUrl: streamUrl,
          videoTitle: videoTitle,
        },
        window.location.origin,
      );
    }

    return (
      <div className="aspect-w-16 aspect-h-9 relative">
        {imageUrl && (
          <Image
            fill
            src={createImageUrl(imageUrl)}
            alt={videoTitle}
            className="object-cover"
            sizes={createImageSizes({
              desktop: "33vw",
              tablet: "50vw",
              mobile: "100vw",
            })}
          />
        )}
        <button
          type="button"
          onClick={handleOverlayClick}
          aria-label="Spela upp video"
          className="absolute inset-0 z-10 flex h-full w-full cursor-pointer items-center justify-center bg-transparent"
        >
          <span
            className={`flex items-center gap-2 rounded-full bg-static-black/80 backdrop-blur-sm ${!!duration && !isLive ? "py-2 pl-2 pr-5" : "p-2"
              }`}
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full">
              <Icon.Play
                className={`h-4 w-4 text-static-white ${!!duration && !isLive ? "translate-x-0.5" : ""
                  }`}
              />
            </span>
            {isLive && (
              <span className="text-md font-semibold text-static-white justify-center pr-3">
                Live
              </span>
            )}

            {!!duration && !isLive && (
              <span className="text-lg font-semibold text-static-white">
                {Math.max(1, Math.round(duration / 60))} min
              </span>
            )}
          </span>
        </button>
      </div>
    );
  }

  if (iframeRef !== null) {
    return (
      <>
        <Script
          src="https://cdn.screen9.com/players/iframe-api.js"
          strategy="afterInteractive"
          onReady={() => setSdkReady(true)}
        />
        <div className="aspect-w-16 aspect-h-9">
          <iframe
            ref={iframeRef}
            title={videoTitle ?? "VideoSpelare"}
            src={src}
            allowFullScreen
          />
        </div>
      </>
    );
  }

  if (imageUrl !== "") {
    return (
      <div className="aspect-w-16 aspect-h-9">
        <Image
          fill
          src={createImageUrl(imageUrl)}
          alt={imageUrl}
          className="object-cover"
          sizes={createImageSizes({
            desktop: "33vw",
            tablet: "50vw",
            mobile: "100vw",
          })}
        />
      </div>
    );
  }
}
